import type { RecoveryRecord, User } from '../types';
import { getLinkedAccounts } from './linkedAccounts';

/**
 * One place to decide what counts as a collection, and whose it is.
 *
 * Six screens used to answer these questions differently: some counted
 * reversed collections, some counted money another agent collected on an
 * account you happen to hold, some filtered by month and some did not. The
 * totals therefore disagreed with the Commission Management Engine, which is
 * the figure the bills are built from.
 *
 * Everything that shows a collection total should go through here.
 */

/** A reversed collection is money that was given back. It is never counted. */
export function activeRecoveries(list: RecoveryRecord[]): RecoveryRecord[] {
  return (list || []).filter((r) => !(r as any).isReversed);
}

/**
 * Whose collection is it?
 *
 * Strictly the agent the money was credited to -- the same test the
 * commission engine uses. Holding the account is NOT the test: an admin or
 * another agent may collect on an account you are allotted, and that money is
 * theirs, not yours. Counting it inflated agents' figures against their bills.
 */
export function isRecoveryCreditedTo(rec: RecoveryRecord, user: User): boolean {
  if (!rec || !user) return false;
  const creditedTo = rec.agentId;
  if (!creditedTo) return false;
  return creditedTo === user.agentId || creditedTo === user.id;
}

/** Collections credited to this user, reversals already removed. */
export function recoveriesForUser(
  list: RecoveryRecord[],
  user: User
): RecoveryRecord[] {
  return activeRecoveries(list).filter((r) => isRecoveryCreditedTo(r, user));
}

/** `YYYY-MM` for a date string, or '' when there isn't one. */
export function monthKeyOf(dateStr?: string): string {
  return dateStr && dateStr.length >= 7 ? dateStr.slice(0, 7) : '';
}

/** `YYYY-MM` for today. */
export function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Keeps only the rows collected in `monthKey`. 'all' keeps everything. */
export function inMonth(
  list: RecoveryRecord[],
  monthKey: string
): RecoveryRecord[] {
  if (!monthKey || monthKey === 'all' || monthKey === 'ALL') return list;
  return list.filter((r) => monthKeyOf(r.recoveryDate) === monthKey);
}

export function sumCollected(list: RecoveryRecord[]): number {
  return (list || []).reduce((sum, r) => sum + (r.amount || 0), 0);
}

export function sumCommission(list: RecoveryRecord[]): number {
  return (list || []).reduce((sum, r) => sum + (r.commissionAmount || 0), 0);
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * "2026-09" -> "September 2026", and the month we are actually in is marked
 * as current. The agent portal used to hard-code August 2026 as "(Current)",
 * so from September onwards the dropdown looked like it was stuck on the
 * previous month even though the figures underneath were correct.
 */
export function formatMonthLabel(monthKey: string): string {
  if (!monthKey || monthKey === 'all' || monthKey === 'ALL') return 'All Months';
  const [y, m] = monthKey.split('-');
  const idx = Number(m) - 1;
  const name = MONTH_NAMES[idx] || monthKey;
  const label = `${name} ${y}`;
  return monthKey === currentMonthKey() ? `${label} (Current)` : label;
}

/**
 * The percentage actually applied to a collection. Falls back to the global
 * default only when the record carries none -- never overrides a stored rate,
 * which is what made the rate column show 10% on every row regardless of the
 * rule the engine had applied.
 */
export function rateOf(rec: RecoveryRecord, defaultRate: number): number {
  const stored = (rec as any).commissionPercentage;
  return typeof stored === 'number' && stored > 0 ? stored : defaultRate;
}

/* ------------------------------------------------------------------ *
 * THE AGENT'S OWN NOTE
 *
 * `agentNotesHistory` holds two very different things mixed together:
 * remarks an agent typed, and entries the app writes for itself --
 * `[Collection Received]: ...`, `[OTS Scheme Calculated]: ...`,
 * `[Customer Classification]: ...`, `[RECOVERY REVERSED]: ...` and about a
 * dozen more.
 *
 * Every screen showed `agentNotesHistory[length - 1]` as "the note", so the
 * newest entry won -- and since the app writes far more entries than an agent
 * does, what you usually saw was the app's own bookkeeping sitting where the
 * agent's remark should be. An agent's note about a customer would be pushed
 * out of sight by the next collection receipt.
 *
 * These two functions separate them. Nothing is deleted or hidden from the
 * full history; only the headline changes.
 * ------------------------------------------------------------------ */

/**
 * True when this entry was written by the app rather than typed by a person.
 *
 * `kind` decides it whenever the entry has one -- every note written from now
 * on is marked at the moment it is created, so nothing has to be guessed.
 *
 * The text patterns below are for the entries already in the database, which
 * carry no mark. The bracketed-tag rule was not enough on its own: allocation
 * and import entries are written as "System: Reallocated from ... " with no
 * brackets at all, so they passed as agent notes and appeared at the top of
 * the customer's Agent Notes -- which is exactly what they should never do.
 */
export function isSystemNote(
  note?: { note?: string; kind?: string; isSystem?: boolean } | null
): boolean {
  if (!note) return true;
  if (note.kind === 'system' || note.isSystem === true) return true;
  if (note.kind === 'user') return false;

  const text = (note.note || '').trim();
  if (!text) return true;

  // A bracketed tag: "[Collection Received]: ...", "[OTS Scheme Calculated] ..."
  if (/^\[[^\]]{2,60}\]\s*:?/.test(text)) return true;

  // Unbracketed entries the app writes about itself.
  return /^(system\s*:|auto\s*:|account data refreshed|reallocated from|system reallocat)/i.test(
    text
  );
}

/** True when a person typed this. The Agent Notes section shows only these. */
export function isUserNote(
  note?: { note?: string; kind?: string; isSystem?: boolean } | null
): boolean {
  return !isSystemNote(note);
}

/** How many notes a person may keep on one customer. */
export const MAX_USER_NOTES_PER_ACCOUNT = 20;

/** Only the notes a person typed, newest first. */
export function userNotesOf<T extends { note?: string; kind?: string }>(
  history?: T[] | null
): T[] {
  return (history || []).filter((n) => isUserNote(n));
}

/** Only the app's own entries, newest first -- the backend activity trail. */
export function systemNotesOf<T extends { note?: string; kind?: string }>(
  history?: T[] | null
): T[] {
  return (history || []).filter((n) => isSystemNote(n));
}

/**
 * The most recent note an AGENT actually wrote, or undefined.
 *
 * Falls back to nothing rather than to a system entry -- showing "no note yet"
 * is accurate, and showing the app's own message as an agent's remark is not.
 */
export function latestAgentNote<T extends { note?: string; date?: string; time?: string }>(
  history?: T[] | null
): T | undefined {
  const list = history || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (!isSystemNote(list[i])) return list[i];
  }
  return undefined;
}

/* ------------------------------------------------------------------ *
 * THE ACCOUNT'S MONEY, WORKED OUT FROM THE RECEIPTS
 *
 * Every money bug in this app has had the same shape. The balance was
 * kept as a running figure: recording a collection subtracted from it,
 * deleting one added back to it, settling an OTS overwrote it with zero,
 * and re-importing a spreadsheet replaced it with the original amount.
 * Four different paths, each adjusting the same number in its own way,
 * and any one of them running twice -- or not at all -- left a figure
 * that corresponded to nothing.
 *
 * The fix is not better arithmetic. It is to stop treating the balance as
 * something to adjust and start treating it as something to CALCULATE:
 *
 *     balance = original outstanding - collections that exist - waiver
 *
 * The original outstanding is written once and never again. The
 * collections are the receipts in the ledger. Delete a receipt and it
 * stops counting the instant it is gone -- there is no "give it back"
 * step to get wrong, and running the calculation ten times gives the same
 * answer as running it once.
 * ------------------------------------------------------------------ */

export interface MoneyFigures {
  originalOutstandingAmount: number;
  totalRecovered: number;
  outstandingAmount: number;
  overdueAmount: number;
  customerBalance: number;
  recoveryStatus: 'Pending' | 'Partially Recovered' | 'Fully Recovered';
}

/** The collections that count: same account, not reversed. */
export function activeRecoveriesFor<T extends { accountId?: string; isReversed?: boolean; amount?: number }>(
  accountId: string,
  recoveries: T[]
): T[] {
  return (recoveries || []).filter(
    (r) => r.accountId === accountId && !r.isReversed
  );
}

/**
 * The original outstanding, for an account that may never have had one.
 *
 * Accounts created before this field existed carry only their current
 * balance. The best reconstruction is what is left plus what has been
 * collected -- which is exact when nothing has drifted, and is in any case
 * the only figure available. It is written once and then left alone.
 */
export function originalOutstandingOf(acc: any): number {
  const stored = Number(acc?.originalOutstandingAmount);
  if (Number.isFinite(stored) && stored > 0) return stored;

  const current = Number(acc?.outstandingAmount) || 0;
  const recovered = Number(acc?.totalRecovered) || 0;
  const reconstructed = current + recovered;
  if (reconstructed > 0) return reconstructed;

  // Last resorts, in order of trustworthiness.
  return Number(acc?.overdueAmount) || Number(acc?.sanctionAmount) || 0;
}

/**
 * THE ORIGINAL BALANCE OF AN ACCOUNT THAT HAS BEEN UNDER A SETTLEMENT.
 *
 * THE BUG THIS FIXES -- cancelling an OTS could leave the SETTLEMENT as the
 * account's original balance, writing off the difference silently.
 *
 * Observed on M/S. LUCKY P O P AND CEMENT (ACC-60305066298), 05-10-2026: the
 * balance stayed at Rs 4,00,000 after the settlement was cancelled, against a
 * true original of Rs 10,02,366, with NOTHING collected. A Rs 6,02,366
 * write-off on an account that had settled nothing.
 *
 * WHY IT HAPPENED. `cancelOTS` restored the original like this:
 *
 *     acc.originalOutstandingAmount || acc.outstandingAmount
 *
 * That second branch is the trap. When the stored original is missing, the
 * CURRENT balance is used instead -- and the current balance of an account
 * under a settlement IS the settlement figure. So "restoring the original"
 * restored the discount.
 *
 * Worse, it then WROTE that figure back as the original, so the account
 * agreed with itself afterwards and Check-and-repair could not see anything
 * wrong. The evidence of the mistake was destroyed by the mistake.
 *
 * WHERE THE REAL FIGURE SURVIVES. Every OTS action -- calculated, modified,
 * confirmed, cancelled -- writes an `otsHistory` entry carrying
 * `originalOutstandingAmount` as it stood AT THAT MOMENT, before any
 * settlement was applied. That record is untouched by the fault, so the true
 * original can be read back out of it.
 *
 * THE TWO CASES THIS CORRECTS, and nothing else:
 *
 *   1. No stored original at all -- where the old code reached for the
 *      current balance. The history is used instead.
 *
 *   2. A stored original that EQUALS one of the account's own recorded
 *      settlement figures. That coincidence is the signature of the
 *      overwrite: a genuine original matches its settlement only when the
 *      concession is zero, and nobody records a settlement that concedes
 *      nothing.
 *
 * An administrator's deliberate correction is left alone, because a figure
 * somebody typed by hand will not equal the settlement. That matters: this
 * must never quietly undo a correction Ashish has already made.
 *
 * Returns 0 when there is nothing to go on, so the caller can decline to
 * guess rather than invent a balance.
 */
export function preSettlementOriginalOf(acc: any): number {
  if (!acc) return 0;

  const stored = Number(acc.originalOutstandingAmount);
  const hasStored = Number.isFinite(stored) && stored > 0;

  // Every settlement figure this account has ever carried.
  const settlementFigures = [
    Number(acc.finalOTSAmount),
    Number(acc.userEnteredOTSAmount),
    Number(acc.otsAgreedAmount),
    Number(acc.systemCalculatedOTSAmount),
    ...(Array.isArray(acc.otsHistory) ? acc.otsHistory : []).flatMap((h: any) => [
      Number(h?.finalOTSAmount),
      Number(h?.userEnteredOTSAmount),
      Number(h?.systemCalculatedOTSAmount),
    ]),
  ].filter((n) => Number.isFinite(n) && n > 0);

  // Rounded to the rupee before comparing: the settlement is rounded when it
  // is calculated, and a stray paisa must not hide the match.
  const storedLooksLikeSettlement =
    hasStored && settlementFigures.some((f) => Math.round(f) === Math.round(stored));

  if (hasStored && !storedLooksLikeSettlement) return stored;

  // The original as the history recorded it, before any settlement was
  // applied. The largest is taken: a settlement only ever moves a balance
  // DOWN, so the highest figure the account has ever been recorded at is the
  // closest thing to the bank's own number.
  const fromHistory = (Array.isArray(acc.otsHistory) ? acc.otsHistory : [])
    .map((h: any) => Number(h?.originalOutstandingAmount))
    .filter((n: number) => Number.isFinite(n) && n > 0);

  const best = fromHistory.length ? Math.max(...fromHistory) : 0;

  // The history is only believed when it is HIGHER than what is stored. If it
  // is not, the stored figure stands -- including a figure an administrator
  // lowered on purpose.
  if (best > 0 && best > (hasStored ? stored : 0)) return best;
  if (hasStored) return stored;

  // No stored original and no usable history. Fall back on the ordinary
  // reconstruction, which is current balance plus what was collected.
  return originalOutstandingOf(acc);
}

/**
 * Recalculate one account's money from the receipts that exist.
 *
 * `waiver` is the agreed OTS concession, which is money the bank has given
 * up rather than money the customer paid -- it reduces the balance without
 * being a collection. Pass 0 when no settlement has been agreed.
 */
export function recalcAccountMoney(
  acc: any,
  recoveriesForAccount: Array<{ amount?: number; isReversed?: boolean }>,
  waiver = 0
): MoneyFigures {
  const original = originalOutstandingOf(acc);

  /**
   * Only the LOAN portion of each collection reduces the balance.
   *
   * A regularisation after NPA can collect more than the account owes,
   * because the bank adds compounding interest and charges on top. That
   * surplus is the customer's money and belongs on the receipt, but it is
   * not repayment of the loan -- counting it here would drive the balance to
   * zero, lose the difference, and hand back an inflated balance if the
   * entry were ever deleted.
   */
  const collected = (recoveriesForAccount || [])
    .filter((r) => !r.isReversed)
    .reduce((sum, r) => {
      const total = Number(r.amount) || 0;
      const excess = Math.max(0, Number((r as any).excessChargesAmount) || 0);
      return sum + Math.max(0, total - excess);
    }, 0);

  const givenUp = Math.max(0, Number(waiver) || 0);
  const outstanding = Math.max(0, original - collected - givenUp);

  /**
   * OVERDUE IS THE BALANCE. It is not a second figure with a memory.
   *
   * THE BUG THIS FIXES -- "the amount never comes back after a delete".
   *
   * This used to read `Math.min(outstanding, storedOverdue)`: the new
   * balance, or whatever overdue was already on the account, whichever was
   * smaller. I wrote that to stop an overdue larger than what is owed, and
   * in doing so made it IMPOSSIBLE FOR OVERDUE TO EVER RISE AGAIN.
   *
   * Collections drive it down -- `recordRecovery` subtracts the loan portion
   * from it directly. Deleting those collections then recalculated the
   * balance correctly, offered the restored figure as the new overdue, and
   * the `min` threw it away in favour of the depressed stored value. A
   * ratchet: down with every collection, never up.
   *
   * Observed on OM SAI STATIONERY AND XEROX CENTER, 01-10-2026: original
   * Rs 9,97,774, collections of Rs 50,000 and Rs 70,000 taken and then both
   * deleted. `outstandingAmount` returned to 9,97,774 and `totalRecovered`
   * to 0 -- both correct -- while `overdueAmount` stayed at 8,77,774, the
   * figure it had reached after the second collection.
   *
   * That stale figure is the one the accounts list prints in red, above the
   * grey "Total:". So the money WAS being returned; the number being read
   * was the one that could not return.
   *
   * There is no separate overdue in the source data to preserve: the
   * importer sets `overdueAmount` to the outstanding balance for every
   * account, and the bank's sheet carries a single balance column
   * (LOAN_B / CUSTOMER_BALANCE). The two should never have diverged.
   */
  const overdue = outstanding;

  const status: MoneyFigures['recoveryStatus'] =
    outstanding <= 0 && collected + givenUp > 0
      ? 'Fully Recovered'
      : collected > 0
      ? 'Partially Recovered'
      : 'Pending';

  return {
    originalOutstandingAmount: original,
    totalRecovered: collected,
    outstandingAmount: outstanding,
    overdueAmount: overdue,
    // One figure, not two that drift apart.
    customerBalance: outstanding,
    recoveryStatus: status,
  };
}

/**
 * THE PRODUCT, AS THE BANK WROTE IT.
 *
 * `productDescription` holds the sheet's own PROD_DESC wording verbatim, and
 * `loanType` holds the category the importer derives from it for filtering
 * and reporting. Screens must show the former: an account that reads
 * differently from the spreadsheet it came from cannot be reconciled with the
 * bank.
 *
 * THE PROBLEM THIS SOLVES FOR ACCOUNTS ALREADY IN THE DATABASE. Keeping
 * PROD_DESC verbatim was only added to the importer recently. Every account
 * uploaded before that has no `productDescription` at all, so it would go on
 * showing the derived category until the whole sheet was uploaded again --
 * and re-uploading a master file to correct one column is not a reasonable
 * thing to ask.
 *
 * It does not have to be asked, because the value was never actually lost.
 * The importer has always stored the entire original spreadsheet row on the
 * account as `customFields`, under whatever header names the bank's own file
 * used. So the true PROD_DESC is already sitting on every existing account,
 * and this reads it from there. Nothing is fetched and nothing is written:
 * the accounts are already in memory, and the correct product appears the
 * moment this build is deployed.
 *
 * Header spellings differ between banks -- "PROD_DESC", "Prod Desc",
 * "PRODUCT_DESCRIPTION" -- so keys are compared with punctuation and case
 * removed rather than matched exactly.
 */
const PRODUCT_HEADER_KEYS = [
  'proddesc',
  'productdesc',
  'productdescription',
  'productdescr',
  'product',
  'prod',
  'loantype',
  'loanproduct',
  'schemedesc',
  'schemedescription',
];

export function productOf(acc: any): string {
  const verbatim = String(acc?.productDescription || '').trim();
  if (verbatim) return verbatim;

  const raw = acc?.customFields;
  if (raw && typeof raw === 'object') {
    // The bank's header, whatever it was called, in the order we prefer it.
    const normalised = new Map<string, string>();
    Object.keys(raw).forEach((key) => {
      const flat = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      const val = String((raw as any)[key] ?? '').trim();
      // First header wins for a given normalised name, and blanks never do.
      if (val && !normalised.has(flat)) normalised.set(flat, val);
    });
    for (const candidate of PRODUCT_HEADER_KEYS) {
      const hit = normalised.get(candidate);
      if (hit) return hit;
    }
  }

  return String(acc?.loanType || '').trim();
}

/**
 * THE OTS CONCESSION, WORKED OUT RATHER THAN REMEMBERED.
 *
 * WHAT THIS REPLACES, AND THE BUG IT FIXES.
 *
 * When a settlement completed, the app calculated the concession once and
 * stored it on the account as `otsDiscountAmount`:
 *
 *     discount = sanctionAmount - agreed settlement
 *
 * Two things were wrong with that line.
 *
 * FIRST, IT USED THE WRONG BASE. A bank's concession is against what the
 * customer OWES, not against the limit that was sanctioned years earlier.
 * On an NPA those two figures are rarely the same -- interest accrues, and
 * the outstanding routinely passes the sanctioned limit. One real account:
 * sanction 9,50,000, outstanding 9,97,774. Every concession calculated this
 * way was wrong by the difference.
 *
 * SECOND, AND WORSE, IT WAS STORED. That figure was then used as the WAIVER
 * whenever the balance was recalculated -- on deleting a collection, on
 * reversing one, and on re-importing the master sheet -- through
 *
 *     outstanding = original - collected - waiver
 *
 * So a stale waiver silently produced a wrong balance, and kept producing
 * one. Change the settlement figure afterwards and the stored concession
 * never moved. On the account above: a collection of 3,90,000 was recorded
 * against a 9,50,000 sanction, storing a concession of 5,60,000. The
 * collection was then deleted, the balance recalculated as
 * 9,97,774 - 0 - 5,60,000, and the customer was shown as owing 4,37,774 --
 * a figure that corresponded to nothing at all. The correct answer was
 * 3,49,221, the settlement they had yet to pay.
 *
 * Derived, it cannot go stale, it cannot disagree with the screen, and
 * recalculating ten times gives the same answer as recalculating once. It is
 * also the formula the OTS window has always displayed as "Actual
 * Concession", so the two finally agree.
 *
 * WHEN IT IS ZERO. No settlement agreed, or the account is not under one.
 * Nothing is given up, so nothing is deducted.
 */
/** The agreed settlement figure, in the order the app trusts them. */
export function otsAgreedOf(acc: any): number {
  if (!acc) return 0;
  return (
    Number(acc.finalOTSAmount) ||
    Number(acc.userEnteredOTSAmount) ||
    Number(acc.otsAgreedAmount) ||
    Number(acc.systemCalculatedOTSAmount) ||
    0
  );
}

export function otsWaiverOf(acc: any): number {
  if (!acc) return 0;

  // A loan linked to a customer-level settlement carries its own share.
  if (typeof acc.otsConcessionAmount === 'number' && acc.customerOtsSettledBy) {
    return Math.max(0, acc.otsConcessionAmount);
  }

  /**
   * THE CONCESSION COUNTS WHEN IT IS EARNED, NOT WHEN IT IS OFFERED.
   *
   * THE CHANGE ASHISH ASKED FOR, 07-10-2026. This used to treat a settlement
   * as applying the moment it was agreed:
   *
   *     accountStatus === 'Closed as per OTS'  ||
   *     otsStatus === 'OTS Settled'            ||
   *     otsStatus === 'OTS Confirmed'          ||
   *     otsActive
   *
   * So applying an OTS took the whole concession off the balance
   * immediately. On M/S. KGN KIDS WEAR that meant Rs 5,47,218 came off the
   * books before the borrower had paid a rupee, and if they never paid, that
   * write-off sat in the figures unnoticed.
   *
   * A settlement OFFER is not a payment. The bank is owed the full amount
   * until the settlement is actually met. So the concession is now only
   * deducted once the settlement is COMPLETE -- the account closed under
   * OTS, or marked settled.
   *
   * WHAT THIS MEANS ON SCREEN. An account with a live settlement shows its
   * FULL balance (original less whatever has been collected), while Collect
   * Pay goes on offering the agreed settlement figure, and the OTS window
   * goes on showing what was agreed. Cancelling the settlement changes
   * nothing about the balance any more, because nothing had been taken off
   * it. Portfolio and overdue totals rise by the sum of all concessions
   * still outstanding -- that is real exposure that was previously hidden.
   *
   * The arithmetic at completion is unchanged: original - collected -
   * concession still lands on zero when the agreed amount has been paid.
   */
  const settlementCompleted =
    acc.accountStatus === 'Closed as per OTS' ||
    acc.accountStatus === 'OTS Settled – Customer Account Closed' ||
    acc.otsStatus === 'OTS Settled' ||
    acc.otsStatus === 'OTS Settled – Customer Account Closed';

  /**
   * COMPLETION BY AMOUNT, NOT BY LABEL. An active OTS whose agreed amount has
   * been collected is complete, whatever its status label says. A status label
   * was not reliable: a settled loan could still read "Recovered" and keep its
   * concession unrecognised.
   */
  const agreedNow = otsAgreedOf(acc);
  const paidByAmount =
    acc.otsActive === true && agreedNow > 0 && (Number(acc.totalRecovered) || 0) >= agreedNow;

  // A live OTS that is not yet paid in full never concedes anything, even if
  // an old status label still says closed.
  if (acc.otsActive === true && agreedNow > 0 && !paidByAmount) return 0;

  if (!settlementCompleted && !paidByAmount) return 0;

  /**
   * CUSTOMER-LEVEL SETTLEMENT. When one OTS covers several loans of the same
   * customer, the concession for each loan is fixed at the moment the agreed
   * amount is met and stored on that loan. Use it as is, so later
   * recalculations (collection deleted, sibling loan re-read) cannot bring
   * back a balance that the settlement cleared.
   */
  if (typeof acc.otsConcessionAmount === 'number') {
    return Math.max(0, acc.otsConcessionAmount);
  }

  // The agreed figure, in the order the rest of the app trusts them.
  const agreed =
    Number(acc.finalOTSAmount) ||
    Number(acc.userEnteredOTSAmount) ||
    Number(acc.otsAgreedAmount) ||
    Number(acc.systemCalculatedOTSAmount) ||
    0;

  if (agreed <= 0) return 0;

  const original = originalOutstandingOf(acc);

  // A settlement above the balance concedes nothing. Never negative.
  return Math.max(0, original - agreed);
}

/**
 * THE BANK THIS ACCOUNT BELONGS TO.
 *
 * THE BUG THIS FIXES. Screens read `account.bankName`. Accounts do not have
 * that field -- the importer writes the bank as `bank`, and nothing anywhere
 * sets `bankName` on an account. Only branches, zones and departments use
 * that name.
 *
 * So `account.bankName` was always undefined, and two things followed:
 *
 *   1. The OTS screen printed its fallback, "Partner Bank", and the WhatsApp
 *      OTS offer printed the Marathi word for "bank" -- in a message sent to
 *      the borrower.
 *
 *   2. Worse and less visible: the bank was passed to `evaluateOTSSlab` to
 *      pick that bank's own OTS rules. Passing undefined meant a BANK-
 *      SPECIFIC OTS SLAB WAS NEVER APPLIED on either screen; both silently
 *      fell back to the default slab.
 *
 * Every read of an account's bank goes through here so the two cannot drift
 * apart again.
 */
export function bankOf(acc: any): string {
  const name =
    (acc?.bank ?? acc?.bankName ?? acc?.bankId ?? '') as string;
  return typeof name === 'string' ? name.trim() : '';
}


/**
 * OTS STATE FOR A WHOLE ACCOUNT LIST, WORKED OUT FROM THE AMOUNTS.
 *
 * Runs on every account load and reads nothing from the database. Customers
 * are linked by the same rule the screens use (getLinkedAccounts).
 *
 *  - When one loan's agreed OTS is collected, that loan closes and every other
 *    open loan of the customer closes with it. Each loan's share of the
 *    concession is fixed to its balance at that point.
 *  - When a collection behind it is deleted, or the OTS is cancelled, the
 *    linked loans reopen at their full balance, and a loan whose OTS is still
 *    agreed but unpaid owes its full balance.
 *
 * Accounts with no OTS involvement are left exactly as they are.
 */
export function applyOtsSettlementState<T extends Record<string, any>>(list: T[]): T[] {
  const idOf = (a: any): string => String(a.accountId || a.id || '');
  const totalOf = (a: any) => Number(a.totalRecovered) || 0;
  const owedOf = (a: any) => Math.max(0, originalOutstandingOf(a) - totalOf(a));

  const changed = new Map<string, Record<string, any>>();
  const put = (a: any, patch: Record<string, any>) => {
    const id = idOf(a);
    changed.set(id, { ...(changed.get(id) || {}), ...patch });
  };

  const settledLoans = list.filter(
    (a: any) =>
      a.otsActive === true &&
      !a.customerOtsSettledBy &&
      otsAgreedOf(a) > 0 &&
      totalOf(a) >= otsAgreedOf(a)
  );
  const settledIds = new Set(settledLoans.map((a: any) => idOf(a)));

  // 1. A settled loan closes, and so does every linked open loan of the customer.
  settledLoans.forEach((L: any) => {
    const lid = idOf(L);
    const lBal = Math.max(0, originalOutstandingOf(L) - totalOf(L) - otsWaiverOf(L));
    put(L, {
      outstandingAmount: lBal,
      overdueAmount: lBal,
      customerBalance: lBal,
      ...(lBal === 0 ? { accountStatus: 'Closed as per OTS', closureType: 'Closed as per OTS' } : {}),
    });

    getLinkedAccounts(L, list as any[]).forEach((m: any) => {
      if (idOf(m) === lid) return;
      if (m.otsActive === true) return; // has its own live settlement
      if (m.customerOtsSettledBy && settledIds.has(m.customerOtsSettledBy) && m.customerOtsSettledBy !== lid) return;
      const linkedHere = m.customerOtsSettledBy === lid && typeof m.otsConcessionAmount === 'number';
      const owed = owedOf(m);
      const share = linkedHere ? Number(m.otsConcessionAmount) : owed;
      const bal = Math.max(0, owed - share);
      put(m, {
        customerOtsSettledBy: lid,
        otsConcessionAmount: share,
        outstandingAmount: bal,
        overdueAmount: bal,
        customerBalance: bal,
        ...(bal === 0 ? { accountStatus: 'Closed as per OTS', closureType: 'Closed as per OTS' } : {}),
      });
    });
  });

  // 2. Everything else that carries OTS state.
  list.forEach((a: any) => {
    if (a.otsActive === true && !settledIds.has(idOf(a))) {
      // Agreed but not paid in full: owes its full balance, agreed figure kept.
      const bal = owedOf(a);
      put(a, {
        outstandingAmount: bal,
        overdueAmount: bal,
        customerBalance: bal,
        ...(bal > 0 && a.accountStatus === 'Closed as per OTS'
          ? { accountStatus: 'Active', closureType: undefined }
          : {}),
      });
      return;
    }
    if (a.customerOtsSettledBy && !settledIds.has(a.customerOtsSettledBy)) {
      // Linked to a settlement that is no longer met: reopen in full.
      const bal = owedOf(a);
      put(a, {
        customerOtsSettledBy: null,
        otsConcessionAmount: null,
        outstandingAmount: bal,
        overdueAmount: bal,
        customerBalance: bal,
        ...(bal > 0 && a.accountStatus === 'Closed as per OTS'
          ? { accountStatus: 'Active', closureType: undefined }
          : {}),
      });
    }
  });

  if (changed.size === 0) return list;
  return list.map((a) => {
    const patch = changed.get(idOf(a));
    return patch ? ({ ...a, ...patch } as T) : a;
  });
}
